import IconCheck from '@apollo/icons/default/IconCheck.svg';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  List,
  ListIcon,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useWizard } from 'react-use-wizard';

import { FlowTypes } from '../../FlowTypeProvider';
import { CommonCloudDedicatedProps, CommonStepProps } from '../../shared';

export type AvailableSubgraphUrlsProps = Pick<
  CommonStepProps,
  'values' | 'setFieldValue'
> &
  Pick<
    CommonCloudDedicatedProps,
    | 'availableUnregisteredResources'
    | 'isDedicatedPlan'
    | 'selectedPrivateDomainName'
    | 'setSelectedPrivateDomainName'
  > & {
    onClose: () => void;
    isOpen: boolean;
    flowType: FlowTypes;
    isVariantOnboardingFlow: boolean;
  };

export const AvailableSubgraphUrls = ({
  values,
  setFieldValue,
  availableUnregisteredResources,
  onClose,
  isOpen,
  selectedPrivateDomainName,
  setSelectedPrivateDomainName,
  isVariantOnboardingFlow,
}: AvailableSubgraphUrlsProps) => {
  const { nextStep } = useWizard();

  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [resourceSelected, setResourceselected] = useState(false);

  const handleListSelectUrl = (domainUrl: string) => {
    setSelectedPrivateDomainName(domainUrl);

    // Set the field value in Formik with default path for the url
    setFieldValue('unregisteredEndpointUrl', `${domainUrl}`);

    // Set the selected domain name to hydrate the next step
    setSelectedPrivateDomainName(domainUrl);
  };

  const handleSelectRegion = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRegion(event.target.value);
    setFieldValue('cloudRegion', event.target.value);
  };

  const filteredResources = availableUnregisteredResources.filter(
    (resource) => !selectedRegion || resource.region.code === selectedRegion,
  );

  const uniqueRegions = availableUnregisteredResources
    .filter(
      (resource, index, self) =>
        index === self.findIndex((r) => r.region.code === resource.region.code),
    )
    .map((resource) => resource.region);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Available Subgraph URLs</ModalTitle>
          <ModalDescription>
            Please select one of the services from this list to create your
            subgraph.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {/* URLs list */}
          {/* Region filter */}
          <FormControl>
            <FormLabel>Choose your service</FormLabel>
            <Select onChange={handleSelectRegion} value={values.cloudRegion}>
              {uniqueRegions.map((region) => (
                <option key={region.code} value={region.code}>
                  {region.name} - {region.code}
                </option>
              ))}
            </Select>
            {resourceSelected && (
              <FormHelperText iconType="loading">
                Linking service to Router...
              </FormHelperText>
            )}
          </FormControl>

          <div className="max-h-52 overflow-y-auto rounded-md border-primary">
            <List isInteractive>
              {filteredResources.map((resource) => {
                const isSelected =
                  selectedPrivateDomainName === resource.domainUrl;

                return (
                  <ListItem
                    key={resource.domainUrl}
                    onClick={() => {
                      setResourceselected(true);
                      handleListSelectUrl(resource.domainUrl || '');

                      onClose();
                      nextStep();

                      if (!isVariantOnboardingFlow) {
                        // We need to call nextStep twice to skip the next step.
                        // We are unable to use goToStep because the stepIndex
                        // is not set for the new flows.
                        // TODO: redesign the stepIndex architecture to allow for
                        // dynamic stepIndex
                        nextStep();
                      }
                    }}
                    isSelected={isSelected}
                  >
                    <div
                      className={classnames(
                        'inline-flex size-6 items-center justify-center rounded-full border border-primary',
                        {
                          'bg-selected': isSelected,
                        },
                      )}
                    >
                      {isSelected && (
                        <ListIcon
                          as={IconCheck}
                          className="size-2 stroke-2 text-icon-white"
                        />
                      )}
                    </div>
                    {resource.domainUrl}
                  </ListItem>
                );
              })}
            </List>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} type="button" variant="secondary">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
